import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import debounce from "lodash/debounce";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { MobileTopBar } from "~mangomap-ui/components/mobile-top-bar/mobile-top-bar";
import type { TitleMenuItem } from "~mangomap-ui/components/title-menu/title-menu";
import { useAdminDrawer } from "~mangomap-ui/components/admin-side-nav/admin-drawer-context";
import type {
	MapPortalData,
	SortOption,
} from "~mangomap-ui/utils/mock-map-portal-data";
import { sortMapsByOption } from "~mangomap-ui/utils/mock-map-portal-data";

import { MapTrifold as MapIcon } from "@phosphor-icons/react/MapTrifold";
import type {
	FilterOptions,
	FilterState,
} from "./components/filters/map-portal-filters/types/filter-types";
import { matchesAccessTypeFilter } from "./components/filters/map-portal-filters/types/filter-types";

import { BulkSelectionProvider } from "./components/bulk-selection-provider";
import { BulkSelectPanel } from "./components/bulk-select-panel/bulk-select-panel";
import { ConfirmDeleteDialog } from "./components/confirm-delete-dialog";
import { MapCard } from "./components/map-card";
import { MapList } from "./components/map-list";
import { PaginationControls } from "./components/pagination-controls";
import { PortalBanner } from "./components/portal-banner";
import { PortalFooter } from "./components/portal-footer";
import { PortalHeader } from "./components/portal-header";
import { PortalSearch, type ViewMode } from "./components/portal-search";
import {
	MapCardSkeleton,
	MapListItemSkeleton,
	SkeletonStack,
} from "~mangomap-ui/components/skeleton/skeleton";
import { dayjs } from "~mangomap-ui/lib/dayjs";
import {
	useMapsAdvancedFilters,
	useMapsSortOption,
	useMapsViewMode,
	useResetMapsEphemeral,
	useSetMapsAdvancedFilters,
	useSetMapsCurrentPage,
	useSetMapsSearchQuery,
	useSetMapsSortOption,
	useSetMapsViewMode,
} from "./states/portal-state";

export interface MapPortalTemplateProps {
	// Essential data and config
	maps?: MapPortalData[];
	portalConfig?: {
		name?: string | null;
		bannerTitle?: string | null;
		bannerContent?: string | null;
		footer?: string | null;
		navigation?: {
			isEnabled?: boolean;
			items?: {
				isEnabled?: boolean;
				label?: string | null;
				type?: string | null;
				linkConfig?: {
					url?: string | null;
					isOpenInNewTab?: boolean;
				} | null;
				emailConfig?: {
					email?: string | null;
					subject?: string | null;
					body?: string | null;
				} | null;
				termsConfig?: {
					content?: string | null;
				} | null;
				modalConfig?: {
					title?: string | null;
					content?: string | null;
				} | null;
			}[];
		} | null;
	} | null;

	// Permissions
	isDeleteAllowed?: (map: MapPortalData) => boolean;

	// Create button props
	createButtonLabel?: string;
	createButtonIcon?: React.ReactNode;
	showCreateButton?: boolean;
	onCreateClick?: () => void;
	logoUrl?: string | null;

	// Essential callbacks
	onMapClick?: (event: { map: MapPortalData }) => void;
	onMapDuplicate?: (event: { map: MapPortalData }) => void;
	onMapDelete?: (event: { map: MapPortalData }) => void;
	onBulkDelete?: (event: { maps: MapPortalData[] }) => Promise<void> | void;

	// Tab toggle props (kept for navigation)
	activeTab?: "maps" | "data";
	onTabChange?: (tab: "maps" | "data") => void;

	// States
	loading?: boolean;
	viewMode?: ViewMode;
	isAuthenticated?: boolean;

	// Mobile top bar — optional. When mobileTopBarTitle is set on mobile the
	// MobileTopBar replaces the default PortalHeader. Desktop is unchanged.
	forceMobile?: boolean;
	mobileTopBarTitle?: string;
	mobileTopBarMenuItems?: TitleMenuItem[];
	mobileTopBarAvatar?: React.ReactNode;
	mobileTopBarPrimaryAction?: React.ReactNode;

	// Portal navigation links
	mapPortalUrl?: string;
	dataPortalUrl?: string;
}

/**
 * MapPortalTemplate - Interactive map portal with search, filtering, and bulk operations
 */
export function MapPortalTemplate({
	// Essential data and config
	maps = [],
	portalConfig,

	// Permissions
	isDeleteAllowed,

	// Create button props
	createButtonLabel = "Create a map",
	createButtonIcon,
	showCreateButton = true,
	onCreateClick,
	logoUrl,

	// Essential callbacks
	onMapClick,
	onMapDuplicate,
	onMapDelete,
	onBulkDelete,

	// Tab toggle props
	activeTab = "maps",
	onTabChange,

	// States
	loading = false,
	viewMode,
	isAuthenticated = true,

	// Mobile top bar
	forceMobile,
	mobileTopBarTitle,
	mobileTopBarMenuItems,
	mobileTopBarAvatar,
	mobileTopBarPrimaryAction,

	// Portal navigation links
	mapPortalUrl,
	dataPortalUrl,
}: MapPortalTemplateProps): React.JSX.Element {
	const { t } = useTranslation();
	const theme = useTheme();
	const mediaIsMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isMobile = forceMobile === true || mediaIsMobile;
	const adminDrawer = useAdminDrawer();
	const mobileTopBarVisible = isMobile && Boolean(mobileTopBarTitle);
	const [searchQuery, setSearchQuery] = React.useState("");
	const [currentPage, setCurrentPage] = React.useState(1);
	const itemsPerPage = 12;
	const internalViewMode = useMapsViewMode();
	const sortOption = useMapsSortOption();
	const advancedFilters = useMapsAdvancedFilters();
	const setMapsSortOption = useSetMapsSortOption();
	const setMapsViewMode = useSetMapsViewMode();
	const setMapsAdvancedFilters = useSetMapsAdvancedFilters();
	const setMapsSearchQuery = useSetMapsSearchQuery();
	const setMapsCurrentPage = useSetMapsCurrentPage();
	const resetMapsEphemeral = useResetMapsEphemeral();
	const hasPersistedMapsViewMode = React.useMemo(() => {
		if (typeof window === "undefined") return false;
		const persistedState = sessionStorage.getItem("portal-template-state");
		return persistedState?.includes('"mapsViewMode"') ?? false;
	}, []);

	React.useEffect(() => {
		if (viewMode && !hasPersistedMapsViewMode) setMapsViewMode(viewMode);
	}, [hasPersistedMapsViewMode, setMapsViewMode, viewMode]);

	React.useEffect(() => {
		return () => {
			resetMapsEphemeral();
		};
	}, [resetMapsEphemeral]);

	// Debounced search handler
	const debouncedSearchChange = React.useMemo(
		() =>
			debounce((query: string) => {
				setSearchQuery(query);
				setCurrentPage(1);
				setMapsSearchQuery(query);
				setMapsCurrentPage(1);
			}, 300),
		[setMapsCurrentPage, setMapsSearchQuery],
	);

	React.useEffect(() => {
		return () => {
			debouncedSearchChange.cancel();
		};
	}, [debouncedSearchChange]);

	// Internal handlers
	const handleInternalSearchChange = React.useCallback(
		(query: string) => {
			debouncedSearchChange(query);
		},
		[debouncedSearchChange],
	);

	const handleInternalViewModeChange = React.useCallback(
		(mode: ViewMode) => {
			setMapsViewMode(mode);
		},
		[setMapsViewMode],
	);

	const handleInternalSortOptionChange = React.useCallback(
		(option: SortOption) => {
			setMapsSortOption(option);
		},
		[setMapsSortOption],
	);

	const handleInternalPageChange = React.useCallback(
		(page: number) => {
			setCurrentPage(page);
			setMapsCurrentPage(page);
		},
		[setMapsCurrentPage],
	);

	// Generate filter options dynamically from maps (map portal specific filters)
	const generatedFilterOptions: FilterOptions = React.useMemo(() => {
		// Extract unique values from maps for map portal filters
		const uniqueAuthors = [
			...new Set(maps.map((map) => map.createdBy).filter(Boolean)),
		];

		// Extract unique access types from maps
		const uniqueAccessTypes = [
			...new Set(maps.map((map) => map.privacy).filter(Boolean)),
		];

		// Calculate date range from map dates
		const dates = maps.flatMap((map) => [map.createdDate, map.lastUpdated]);

		const minDate =
			dates.length > 0
				? dayjs
						.unix(Math.min(...dates.map((d) => dayjs(d).unix())))
						.format("YYYY-MM-DD")
				: dayjs().format("YYYY-MM-DD");
		const maxDate =
			dates.length > 0
				? dayjs
						.unix(Math.max(...dates.map((d) => dayjs(d).unix())))
						.format("YYYY-MM-DD")
				: dayjs().format("YYYY-MM-DD");

		return {
			// Map portal doesn't need these filters
			availableCategories: [],
			availableFormats: [],
			availableGeometries: [],
			availableTags: [],
			sizeRange: [0, 0],

			// Map portal specific filters
			availableAuthors: uniqueAuthors, // Owner filter
			availableAccessTypes:
				uniqueAccessTypes.length > 0
					? uniqueAccessTypes
					: ["public", "private", "restricted"], // Access type filter
			dateRange: {
				min: minDate,
				max: maxDate,
			}, // Date filter
		};
	}, [maps]);

	const effectiveFilterOptions = generatedFilterOptions;

	// Advanced filter handlers
	const handleAdvancedFiltersApply = (filterState: FilterState) => {
		setMapsAdvancedFilters(filterState);
		setCurrentPage(1);
		setMapsCurrentPage(1);
	};

	const handleAdvancedFiltersClear = () => {
		setMapsAdvancedFilters({});
		setCurrentPage(1);
		setMapsCurrentPage(1);
	};

	// Helper function to apply advanced filters (map portal specific)
	const applyAdvancedFilters = React.useCallback(
		(maps: MapPortalData[], filters: Partial<FilterState>): MapPortalData[] => {
			return maps.filter((map) => {
				const selectedAccessTypes = filters.accessTypes ?? [];
				if (
					selectedAccessTypes.length > 0 &&
					!matchesAccessTypeFilter(map.privacy, selectedAccessTypes)
				)
					return false;

				// Date range filter
				if (
					filters.dateRange?.start !== undefined ||
					filters.dateRange?.end !== undefined
				) {
					const start = dayjs(filters.dateRange.start).startOf("day");
					const end = dayjs(filters.dateRange.end).endOf("day");
					if (
						filters.dateRange.start &&
						dayjs(map.createdDate).isBefore(start) &&
						dayjs(map.lastUpdated).isBefore(start)
					)
						return false;
					if (
						filters.dateRange.end &&
						dayjs(map.createdDate).isAfter(end) &&
						dayjs(map.lastUpdated).isAfter(end)
					)
						return false;
				}

				// Owner filter (authors)
				if (
					filters.authors &&
					filters.authors.length > 0 &&
					!filters.authors.includes(map.createdBy)
				)
					return false;

				return true;
			});
		},
		[],
	);

	const canDeleteMap = React.useCallback(
		(map: MapPortalData) => isDeleteAllowed?.(map) ?? false,
		[isDeleteAllowed],
	);

	// Delete confirmation dialog state
	const [pendingDeleteMaps, setPendingDeleteMaps] = React.useState<
		MapPortalData[]
	>([]);
	const [bulkDeleteLoading, setBulkDeleteLoading] = React.useState(false);
	const [bulkDeleteCompleted, setBulkDeleteCompleted] = React.useState(0);

	const handleDeleteRequest = React.useCallback(
		(event: { map: MapPortalData }) => {
			setPendingDeleteMaps([event.map]);
		},
		[],
	);

	const handleBulkDeleteRequest = React.useCallback(
		(event: { maps: MapPortalData[] }) => {
			setPendingDeleteMaps(event.maps);
		},
		[],
	);

	const handleDeleteConfirm = React.useCallback(async () => {
		if (pendingDeleteMaps.length === 0) return;

		if (pendingDeleteMaps.length === 1) {
			onMapDelete?.({ map: pendingDeleteMaps[0]! });
			setPendingDeleteMaps([]);
		} else {
			setBulkDeleteLoading(true);
			try {
				await onBulkDelete?.({ maps: pendingDeleteMaps });
				setPendingDeleteMaps([]);
				setBulkDeleteCompleted((count) => count + 1);
			} finally {
				setBulkDeleteLoading(false);
			}
		}
	}, [pendingDeleteMaps, onMapDelete, onBulkDelete]);

	const handleDeleteCancel = React.useCallback(() => {
		setPendingDeleteMaps([]);
	}, []);

	// Filter and search maps
	const filteredMaps = React.useMemo(() => {
		let result = maps;

		// Apply search query filtering
		if (searchQuery.trim()) {
			const searchLower = searchQuery.toLowerCase().trim();
			result = result.filter(
				(map) =>
					map.title.toLowerCase().includes(searchLower) ||
					map.description.toLowerCase().includes(searchLower) ||
					map.createdBy.toLowerCase().includes(searchLower) ||
					map.tags.some((tag) => tag.toLowerCase().includes(searchLower)),
			);
		}

		// Apply advanced filters
		result = applyAdvancedFilters(result, advancedFilters);

		// Apply sorting
		result = sortMapsByOption(result, sortOption);

		return result;
	}, [maps, searchQuery, sortOption, advancedFilters, applyAdvancedFilters]);

	const hasDeletableMaps = React.useMemo(
		() => filteredMaps.some(canDeleteMap),
		[filteredMaps, canDeleteMap],
	);

	// Calculate pagination
	const totalItems = filteredMaps.length;
	const totalPages = Math.ceil(totalItems / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const paginatedMaps = filteredMaps.slice(startIndex, endIndex);

	return (
		<Box
			sx={{
				minHeight: "100vh",
				bgcolor: "var(--mui-palette-background-default)",
			}}>
			{/* Header — desktop / view-only mobile */}
			{!mobileTopBarVisible && (
				<PortalHeader
					portalName={portalConfig?.name ?? ""}
					logoUrl={logoUrl}
					createButtonLabel={createButtonLabel}
					createButtonIcon={createButtonIcon}
					onCreateClick={onCreateClick}
					showCreateButton={showCreateButton}
					loading={loading}
					navigationItems={portalConfig?.navigation?.items ?? []}
					mapPortalUrl={mapPortalUrl}
					dataPortalUrl={dataPortalUrl}
				/>
			)}

			{/* Mobile top bar (authed chrome) — sticky so it stays visible while scrolling */}
			{mobileTopBarVisible && (
				<Box
					sx={{
						position: "sticky",
						top: 0,
						zIndex: 1100,
					}}>
					<MobileTopBar
						title={mobileTopBarTitle!}
						titleMenuItems={mobileTopBarMenuItems}
						titleAvatar={mobileTopBarAvatar}
						primaryAction={mobileTopBarPrimaryAction}
						onMenuOpen={adminDrawer.open}
					/>
				</Box>
			)}

			{/* Welcome Banner — hidden on mobile; the banner is surfaced inside
			    the mobile nav side drawer instead via `MobilePortalDrawerContent`. */}
			{!isMobile && (
				<PortalBanner
					headline={portalConfig?.bannerTitle ?? undefined}
					bodyLines={portalConfig?.bannerContent ?? undefined}
				/>
			)}

			{/* Search and Filters */}
			<Container
				maxWidth="xl"
				component="header"
				disableGutters
				sx={{
					position: "sticky",
					top: mobileTopBarVisible ? 56 : 73,
					zIndex: 100,
				}}>
				<Box sx={{ px: { xs: 0, sm: 3 }, py: { xs: 0, sm: 2 } }}>
					<PortalSearch
						variant="maps"
						searchQuery={searchQuery}
						onSearchChange={handleInternalSearchChange}
						viewMode={internalViewMode}
						onViewModeChange={handleInternalViewModeChange}
						sortOption={sortOption}
						onSortOptionChange={handleInternalSortOptionChange}
						loading={loading}
						activeTab={activeTab}
						onTabChange={onTabChange}
						advancedFilters={advancedFilters}
						filterOptions={isAuthenticated ? effectiveFilterOptions : undefined}
						onAdvancedFiltersApply={handleAdvancedFiltersApply}
						onAdvancedFiltersClear={handleAdvancedFiltersClear}
					/>
				</Box>
			</Container>

			{/* Main Content */}
			<Container maxWidth="xl">
				<Box sx={{ px: { xs: 0, sm: 3 }, py: { xs: 2, sm: 3 } }}>
					{loading ? (
						<LoadingSkeletons viewMode={internalViewMode} />
					) : !hasDeletableMaps ? (
						// No bulk selection UI: When user cannot delete maps
						<>
							{/* Maps Grid/List */}
							{internalViewMode === "card" ? (
								paginatedMaps.length === 0 ? (
									<Box
										sx={{
											textAlign: "center",
											py: 8,
											px: 3,
										}}>
										<MapIcon
											size={48}
											color="var(--mui-palette-text-disabled)"
										/>
										<Typography
											variant="h6"
											color="text.secondary"
											sx={{ mt: 2, mb: 1 }}>
											{t("portal.results.emptyTitle")}
										</Typography>
										<Typography
											variant="body2"
											color="text.secondary">
											{t("portal.results.emptyGuidance")}
										</Typography>
									</Box>
								) : (
									<Grid
										container
										spacing={3}>
										{paginatedMaps.map((map) => (
											<Grid
												key={map.id}
												size={{
													xl: 4, // 3 cards per row on extra large screens
													lg: 4, // 3 cards per row on large screens
													md: 6, // 2 cards per row on medium screens
													sm: 12, // 1 card per row on small screens
													xs: 12, // 1 card per row on extra small screens
												}}>
												<MapCard
													map={map}
													href={map.href}
													onClick={(event) => onMapClick?.(event)}
													onDuplicate={(event) => onMapDuplicate?.(event)}
													onDelete={handleDeleteRequest}
													loading={loading ?? false}
													canDelete={canDeleteMap(map)}
												/>
											</Grid>
										))}
									</Grid>
								)
							) : (
								<MapList
									maps={paginatedMaps}
									onMapClick={(event) => onMapClick?.(event)}
									onMapDuplicate={(event) => onMapDuplicate?.(event)}
									onMapDelete={handleDeleteRequest}
									loading={loading ?? false}
									canDelete={canDeleteMap}
								/>
							)}

							{/* Pagination */}
							{totalPages > 1 && (
								<PaginationControls
									currentPage={currentPage}
									totalPages={totalPages}
									totalItems={totalItems}
									itemsPerPage={itemsPerPage}
									onPageChange={handleInternalPageChange}
									loading={loading}
								/>
							)}
						</>
					) : (
						// Bulk selection UI: When user can delete maps
						<BulkSelectionProvider
							maps={filteredMaps}
							canSelect={canDeleteMap}
							clearSelectionOn={bulkDeleteCompleted}>
							{/* Maps Grid/List */}
							{internalViewMode === "card" ? (
								paginatedMaps.length === 0 ? (
									<Box
										sx={{
											textAlign: "center",
											py: 8,
											px: 3,
										}}>
										<MapIcon
											size={48}
											color="var(--mui-palette-text-disabled)"
										/>
										<Typography
											variant="h6"
											color="text.secondary"
											sx={{ mt: 2, mb: 1 }}>
											{t("portal.results.emptyTitle")}
										</Typography>
										<Typography
											variant="body2"
											color="text.secondary">
											{t("portal.results.emptyGuidance")}
										</Typography>
									</Box>
								) : (
									<Grid
										container
										spacing={3}>
										{paginatedMaps.map((map) => (
											<Grid
												key={map.id}
												size={{
													xl: 4, // 3 cards per row on extra large screens
													lg: 4, // 3 cards per row on large screens
													md: 6, // 2 cards per row on medium screens
													sm: 12, // 1 card per row on small screens
													xs: 12, // 1 card per row on extra small screens
												}}>
												<MapCard
													map={map}
													href={map.href}
													onClick={(event) => onMapClick?.(event)}
													onDuplicate={(event) => onMapDuplicate?.(event)}
													onDelete={handleDeleteRequest}
													loading={loading ?? false}
													canDelete={canDeleteMap(map)}
												/>
											</Grid>
										))}
									</Grid>
								)
							) : (
								<MapList
									maps={paginatedMaps}
									onMapClick={(event) => onMapClick?.(event)}
									onMapDuplicate={(event) => onMapDuplicate?.(event)}
									onMapDelete={handleDeleteRequest}
									loading={loading ?? false}
									canDelete={canDeleteMap}
								/>
							)}

							{/* Pagination */}
							{totalPages > 1 && (
								<PaginationControls
									currentPage={currentPage}
									totalPages={totalPages}
									totalItems={totalItems}
									itemsPerPage={itemsPerPage}
									onPageChange={handleInternalPageChange}
									loading={loading}
								/>
							)}

							<BulkSelectPanel
								onBulkDelete={handleBulkDeleteRequest}
								loading={bulkDeleteLoading}
							/>
						</BulkSelectionProvider>
					)}
				</Box>
			</Container>

			{/* Footer */}
			<PortalFooter footerContent={portalConfig?.footer ?? ""} />

			{/* Delete Confirmation Dialog */}
			<ConfirmDeleteDialog
				open={pendingDeleteMaps.length > 0}
				onClose={handleDeleteCancel}
				onConfirm={handleDeleteConfirm}
				maps={pendingDeleteMaps}
				loading={bulkDeleteLoading}
				activeTab={activeTab}
			/>
		</Box>
	);
}

function LoadingSkeletons({ viewMode }: { viewMode: ViewMode }) {
	const skeletonCount = 12;

	if (viewMode === "card") {
		return (
			<Grid
				container
				spacing={{ xs: 1, sm: 2, md: 3 }}>
				{Array.from({ length: skeletonCount }, (_, index) => (
					<Grid
						key={index}
						size={{
							xl: 4,
							lg: 4,
							md: 6,
							sm: 12,
							xs: 12,
						}}>
						<MapCardSkeleton />
					</Grid>
				))}
			</Grid>
		);
	}

	// List view
	return (
		<SkeletonStack spacing={1}>
			{Array.from({ length: skeletonCount }, (_, index) => (
				<MapListItemSkeleton key={index} />
			))}
		</SkeletonStack>
	);
}
